import { useState, useEffect, useCallback } from 'react';
import Header from '../components/Header';
import api from '../utils/api';
import { useToast } from '../context/ToastContext';

const MONTHS_AR = ['يناير','فبراير','مارس','أبريل','مايو','يونيو','يوليو','أغسطس','سبتمبر','أكتوبر','نوفمبر','ديسمبر'];

const ITEM_TYPES = [
  { id: 1, key: 'type1', ar: 'دجاج مبرد',    maxKey: 'max_qty_type1', color: '#047857' },
  { id: 3, key: 'type3', ar: 'دجاج مجمد',    maxKey: 'max_qty_type3', color: '#1d4ed8' },
  { id: 4, key: 'type4', ar: 'مقطعات مبرد',  maxKey: 'max_qty_type4', color: '#b45309' },
  { id: 5, key: 'type5', ar: 'دجاج مبرد GB', maxKey: 'max_qty_type5', color: '#7c3aed' },
];

function getCurrentPeriod() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function getPrevPeriod(period) {
  const [y, m] = period.split('-').map(Number);
  const d = new Date(y, m - 2, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function fmt(n) { return parseFloat(n || 0).toLocaleString('ar', { maximumFractionDigits: 0 }); }

export default function TargetsPage() {
  const [period, setPeriod]     = useState(getCurrentPeriod());
  const [users, setUsers]       = useState([]);
  const [targets, setTargets]   = useState({});
  const [actuals, setActuals]   = useState({});
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(null);
  const [copying, setCopying]   = useState(false);
  const [editUser, setEditUser] = useState(null);
  const toast = useToast();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [usersData, targetsData] = await Promise.all([
        api.get('/users'),
        api.get(`/targets?period=${period}`),
      ]);
      const filtered = usersData.filter(u =>
        ['salesman','supervisor','area_supervisor'].includes(u.role) && u.active
      );
      setUsers(filtered);
      const tMap = {};
      targetsData.forEach(t => { tMap[t.user_id] = t; });
      setTargets(tMap);

      // Load actuals for all users
      const aMap = {};
      await Promise.all(filtered.map(async u => {
        try {
          const a = await api.get(`/netsuite/actuals/${u.id}/${period}`);
          if (a) aMap[u.id] = a;
        } catch {}
      }));
      setActuals(aMap);
    } catch { toast.error('خطأ في التحميل'); }
    finally { setLoading(false); }
  }, [period]);

  useEffect(() => { loadData(); }, [loadData]);

  async function saveTarget(userId, form) {
    setSaving(userId);
    try {
      await api.post('/targets', {
        user_id: userId, period,
        qty_type1_target:         parseFloat(form.qty_type1_target         || 0),
        qty_type3_target:         parseFloat(form.qty_type3_target         || 0),
        qty_type4_target:         parseFloat(form.qty_type4_target         || 0),
        qty_type5_target:         parseFloat(form.qty_type5_target         || 0),
        bad_return_threshold:     parseFloat(form.bad_return_threshold      || 3),
        coverage_target:          parseInt(form.coverage_target             || 0),
        collection_threshold:     parseFloat(form.collection_threshold      || 15),
        collection_target_amount: parseFloat(form.collection_target_amount  || 0),
      });
      toast.success('تم حفظ الأهداف');
      setEditUser(null);
      await loadData();
    } catch (err) { toast.error(err.message || 'خطأ في الحفظ'); }
    finally { setSaving(null); }
  }

  async function copyFromLastMonth() {
    const prev = getPrevPeriod(period);
    setCopying(true);
    try {
      const prevTargets = await api.get(`/targets?period=${prev}`);
      if (!prevTargets.length) { toast.error('لا توجد أهداف في الشهر السابق'); return; }
      let copied = 0;
      for (const t of prevTargets) {
        if (!targets[t.user_id]) {
          await api.post('/targets', {
            user_id: t.user_id, period,
            qty_type1_target:         t.qty_type1_target         || 0,
            qty_type3_target:         t.qty_type3_target         || 0,
            qty_type4_target:         t.qty_type4_target         || 0,
            qty_type5_target:         t.qty_type5_target         || 0,
            bad_return_threshold:     t.bad_return_threshold      || 3,
            coverage_target:          t.coverage_target           || 0,
            collection_threshold:     t.collection_threshold      || 15,
            collection_target_amount: t.collection_target_amount  || 0,
          });
          copied++;
        }
      }
      toast.success(`تم نسخ ${copied} هدف`);
      await loadData();
    } catch (err) { toast.error(err.message); }
    finally { setCopying(false); }
  }

  const periods = [];
  const now = new Date();
  for (let i = -1; i < 5; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    periods.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }

  return (
    <div className="page">
      <Header title="الأهداف الشهرية" />
      <div className="page-content">
        <div style={styles.periodScroll}>
          {periods.map(p => (
            <button key={p} style={{
              ...styles.periodBtn,
              background: p === period ? 'var(--primary)' : 'white',
              color:      p === period ? 'white' : 'var(--gray-600)',
              border:     `1.5px solid ${p === period ? 'var(--primary)' : 'var(--gray-200)'}`,
            }} onClick={() => setPeriod(p)}>
              {MONTHS_AR[parseInt(p.split('-')[1]) - 1]} {p.split('-')[0]}
            </button>
          ))}
        </div>

        <div style={styles.statsRow}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
            <span style={{ fontSize: 20, fontWeight: 800, color: 'var(--primary)' }}>{Object.keys(targets).length}/{users.length}</span>
            <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>أهداف محددة</span>
          </div>
          <button className="btn btn-outline btn-sm" onClick={copyFromLastMonth} disabled={copying || loading} style={{ fontSize: 12 }}>
            {copying ? 'جاري...' : '📋 نسخ من الشهر السابق'}
          </button>
        </div>

        {loading ? <div className="loading-page"><div className="spinner" /></div> : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {users.map(user => (
              <UserTargetRow
                key={user.id}
                user={user}
                target={targets[user.id]}
                actual={actuals[user.id]}
                isEditing={editUser === user.id}
                saving={saving === user.id}
                onEdit={() => setEditUser(editUser === user.id ? null : user.id)}
                onSave={(form) => saveTarget(user.id, form)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function UserTargetRow({ user, target, actual, isEditing, saving, onEdit, onSave }) {
  const [form, setForm] = useState({
    qty_type1_target:         target?.qty_type1_target         || '',
    qty_type3_target:         target?.qty_type3_target         || '',
    qty_type4_target:         target?.qty_type4_target         || '',
    qty_type5_target:         target?.qty_type5_target         || '',
    bad_return_threshold:     target?.bad_return_threshold      || '3',
    coverage_target:          target?.coverage_target           || '',
    collection_threshold:     target?.collection_threshold      || '15',
    collection_target_amount: target?.collection_target_amount  || '',
  });

  useEffect(() => {
    if (target) setForm({
      qty_type1_target:         target.qty_type1_target         || '',
      qty_type3_target:         target.qty_type3_target         || '',
      qty_type4_target:         target.qty_type4_target         || '',
      qty_type5_target:         target.qty_type5_target         || '',
      bad_return_threshold:     target.bad_return_threshold      || '3',
      coverage_target:          target.coverage_target           || '',
      collection_threshold:     target.collection_threshold      || '15',
      collection_target_amount: target.collection_target_amount  || '',
    });
  }, [target]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const hasTarget = !!target;

  return (
    <div style={styles.userCard}>
      <div style={styles.userHeader} onClick={onEdit}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={styles.avatar}>{user.name_ar?.charAt(0) || 'م'}</div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700 }}>{user.name_ar}</div>
            <div style={{ fontSize: 11, color: 'var(--gray-400)' }}>{user.username} · {user.branch_name_ar || '—'}</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 10, background: hasTarget ? 'var(--green-100)' : 'var(--gray-100)', color: hasTarget ? 'var(--green-700)' : 'var(--gray-500)' }}>
            {hasTarget ? '✓ محدد' : 'غير محدد'}
          </span>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--gray-400)" strokeWidth="2"
               style={{ transform: isEditing ? 'rotate(180deg)' : 'none', transition: '0.2s' }}>
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </div>
      </div>

      {/* Preview: Target vs Actual */}
      {!isEditing && (hasTarget || actual) && (
        <div style={styles.preview}>
          {ITEM_TYPES.map(type => {
            const tval = parseFloat(target?.[`qty_${type.key}_target`] || 0);
            const aval = parseFloat(actual?.[`qty_${type.key}_actual`]  || 0);
            if (tval === 0 && aval === 0) return null;
            const pct = tval > 0 ? Math.min((aval / tval) * 100, 100) : 0;
            return (
              <div key={type.id} style={styles.previewItem}>
                <div style={{ fontSize: 9, color: type.color, fontWeight: 700 }}>{type.ar}</div>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--gray-800)' }}>
                  {fmt(aval)} / {fmt(tval)}
                </div>
                <div className="kpi-bar" style={{ marginTop: 2 }}>
                  <div className={`kpi-bar-fill ${pct >= 90 ? 'green' : pct >= 60 ? 'yellow' : 'red'}`}
                       style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          }).filter(Boolean)}
          {/* Collection preview */}
          {target?.collection_target_amount > 0 && (
            <div style={styles.previewItem}>
              <div style={{ fontSize: 9, color: '#7c3aed', fontWeight: 700 }}>التحصيل</div>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--gray-800)' }}>
                {parseFloat(actual?.collection_actual_pct || 0).toFixed(1)}%
              </div>
              <div style={{ fontSize: 9, color: 'var(--gray-400)' }}>
                الرصيد: {fmt(target.collection_target_amount)} ر.س
              </div>
            </div>
          )}
        </div>
      )}

      {isEditing && (
        <div>
          <div className="divider" style={{ margin: '12px 0' }} />
          <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--gray-600)', marginBottom: 10 }}>أهداف الكميات حسب نوع المنتج</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {ITEM_TYPES.map(type => (
              <div key={type.id} className="input-group" style={{ margin: 0 }}>
                <label className="input-label" style={{ color: type.color }}>
                  {type.ar} (قطعة)
                </label>
                <input className="input" type="number" value={form[`qty_${type.key}_target`]}
                  onChange={e => set(`qty_${type.key}_target`, e.target.value)} placeholder="0" />
              </div>
            ))}
          </div>
          <div className="divider" style={{ margin: '12px 0' }} />
          <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--gray-600)', marginBottom: 10 }}>المؤشرات الأخرى</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div className="input-group" style={{ margin: 0 }}>
              <label className="input-label">حد المرتجع الرديء (%)</label>
              <input className="input" type="number" step="0.1" value={form.bad_return_threshold}
                onChange={e => set('bad_return_threshold', e.target.value)} placeholder="3" />
            </div>
            <div className="input-group" style={{ margin: 0 }}>
              <label className="input-label">هدف التغطية (عميل)</label>
              <input className="input" type="number" value={form.coverage_target}
                onChange={e => set('coverage_target', e.target.value)} placeholder="0" />
            </div>
            <div className="input-group" style={{ margin: 0 }}>
              <label className="input-label">حد التحصيل (%)</label>
              <input className="input" type="number" step="0.1" value={form.collection_threshold}
                onChange={e => set('collection_threshold', e.target.value)} placeholder="15" />
            </div>
            <div className="input-group" style={{ margin: 0 }}>
              <label className="input-label">الرصيد المستهدف (ر.س)</label>
              <input className="input" type="number" value={form.collection_target_amount}
                onChange={e => set('collection_target_amount', e.target.value)} placeholder="0" />
            </div>
          </div>
          <button className="btn btn-primary btn-full" style={{ marginTop: 12 }} onClick={() => onSave(form)} disabled={saving}>
            {saving ? <><div className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} /> جاري الحفظ...</> : 'حفظ الأهداف'}
          </button>
        </div>
      )}
    </div>
  );
}

const styles = {
  periodScroll: { display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 12, marginBottom: 4, scrollbarWidth: 'none' },
  periodBtn: { padding: '6px 14px', borderRadius: 20, fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: 'var(--font-main)', transition: 'all 0.2s' },
  statsRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 },
  userCard: { background: 'white', borderRadius: 14, padding: '14px 16px', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--gray-100)' },
  userHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' },
  avatar: { width: 38, height: 38, borderRadius: '50%', background: 'linear-gradient(135deg, var(--primary), var(--green-400))', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 700, flexShrink: 0 },
  preview: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--gray-100)' },
  previewItem: { display: 'flex', flexDirection: 'column', gap: 2 },
};
